import React, { createContext, useContext, useReducer, ReactNode } from 'react';
import { AppState, Job, ActionBar } from '../types';

interface AppContextType {
  state: AppState;
  setCurrentJob: (job: Job | null) => void;
  updateActionBar: (barIndex: number, actionBar: ActionBar) => void;
  toggleKeybindMode: () => void;
  setKeyBinding: (barIndex: number, slotIndex: number, key: string) => void;
  toggleActionBarLock: () => void;
  setGcd: (gcd: number) => void;
}

const initialState: AppState = {
  currentJob: null,
  actionBars: [
    { slots: Array(12).fill(null), keyBindings: {} },
    { slots: Array(12).fill(null), keyBindings: {} },
    { slots: Array(12).fill(null), keyBindings: {} }
  ],
  isKeybindMode: false,
  isActionBarLocked: true
};

const AppContext = createContext<AppContextType | undefined>(undefined);

type Action = 
  | { type: 'SET_CURRENT_JOB'; payload: Job | null }
  | { type: 'UPDATE_ACTION_BAR'; payload: { barIndex: number; actionBar: ActionBar } }
  | { type: 'TOGGLE_KEYBIND_MODE' }
  | { type: 'SET_KEY_BINDING'; payload: { barIndex: number; slotIndex: number; key: string } }
  | { type: 'TOGGLE_ACTION_BAR_LOCK' }
  | { type: 'SET_GCD'; payload: number };

function appReducer(state: AppState, action: Action): AppState {
  switch (action.type) {
    case 'SET_CURRENT_JOB':
      return { ...state, currentJob: action.payload };
    case 'UPDATE_ACTION_BAR':
      const newActionBars = [...state.actionBars];
      newActionBars[action.payload.barIndex] = action.payload.actionBar;
      return { ...state, actionBars: newActionBars };
    case 'TOGGLE_KEYBIND_MODE':
      return { ...state, isKeybindMode: !state.isKeybindMode };
    case 'SET_KEY_BINDING':
      const updatedActionBars = [...state.actionBars];
      updatedActionBars[action.payload.barIndex].keyBindings[action.payload.key] = action.payload.slotIndex;
      return { ...state, actionBars: updatedActionBars };
    case 'TOGGLE_ACTION_BAR_LOCK':
      return { ...state, isActionBarLocked: !state.isActionBarLocked };
    case 'SET_GCD':
      if (!state.currentJob) return state;
      return { 
        ...state, 
        currentJob: { 
          ...state.currentJob, 
          gcd: action.payload 
        } 
      };
    default:
      return state;
  }
}

export function AppProvider({ children }: { children: ReactNode }) {
  const [state, dispatch] = useReducer(appReducer, initialState);

  const setCurrentJob = (job: Job | null) => {
    // 如果职业没有GCD配置，设置默认值2.5
    const jobWithGcd = job ? { ...job, gcd: job.gcd || 2.5 } : null;
    dispatch({ type: 'SET_CURRENT_JOB', payload: jobWithGcd });
  };

  const updateActionBar = (barIndex: number, actionBar: ActionBar) => {
    dispatch({ type: 'UPDATE_ACTION_BAR', payload: { barIndex, actionBar } });
  };

  const toggleKeybindMode = () => {
    dispatch({ type: 'TOGGLE_KEYBIND_MODE' });
  };

  const setKeyBinding = (barIndex: number, slotIndex: number, key: string) => {
    dispatch({ type: 'SET_KEY_BINDING', payload: { barIndex, slotIndex, key } });
  };

  const toggleActionBarLock = () => {
    dispatch({ type: 'TOGGLE_ACTION_BAR_LOCK' });
  };

  const setGcd = (gcd: number) => {
    dispatch({ type: 'SET_GCD', payload: gcd });
  };

  return (
    <AppContext.Provider value={{
      state,
      setCurrentJob,
      updateActionBar,
      toggleKeybindMode,
      setKeyBinding,
      toggleActionBarLock,
      setGcd
    }}>
      {children}
    </AppContext.Provider>
  );
}

export function useApp() {
  const context = useContext(AppContext);
  if (context === undefined) {
    throw new Error('useApp must be used within an AppProvider');
  }
  return context;
}